import { useState, useRef, useCallback, useEffect } from 'react';
import api from '../utils/api';

/**
 * Hook for real-time speech-to-text transcription using AssemblyAI Universal Streaming v3
 * Uses direct WebSocket connection for browser compatibility
 *
 * Usage:
 * const { isRecording, transcript, startRecording, stopRecording, error, connectionStatus } = useRealtimeTranscription();
 */

const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY = 2000;
const SAMPLE_RATE = 16000;

const useRealtimeTranscription = (options = {}) => {
    const {
        onTranscript,           // Callback when new transcript received
        onFinalTranscript,      // Callback when final transcript (end of turn) received
        onError,                // Callback when error occurs
        onReconnecting,         // Callback when reconnecting
    } = options;

    const [isRecording, setIsRecording] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [interimTranscript, setInterimTranscript] = useState('');
    const [error, setError] = useState(null);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');

    const wsRef = useRef(null);
    const streamRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const isStoppingRef = useRef(false);
    const audioContextRef = useRef(null);
    const processorRef = useRef(null);
    const sourceRef = useRef(null);

    // Cleanup function
    const cleanup = useCallback(() => {
        if (processorRef.current) {
            try {
                processorRef.current.disconnect();
            } catch (e) { /* ignore */ }
            processorRef.current = null;
        }

        if (sourceRef.current) {
            try {
                sourceRef.current.disconnect();
            } catch (e) { /* ignore */ }
            sourceRef.current = null;
        }

        if (audioContextRef.current) {
            try {
                audioContextRef.current.close();
            } catch (e) { /* ignore */ }
            audioContextRef.current = null;
        }

        if (wsRef.current) {
            try {
                wsRef.current.close(1000, 'User stopped');
            } catch (e) { /* ignore */ }
            wsRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        setConnectionStatus('disconnected');
    }, []);

    // Convert Float32 audio to PCM16 (required by AssemblyAI)
    const floatTo16BitPCM = useCallback((float32Array) => {
        const buffer = new ArrayBuffer(float32Array.length * 2);
        const view = new DataView(buffer);
        for (let i = 0; i < float32Array.length; i++) {
            const s = Math.max(-1, Math.min(1, float32Array[i]));
            view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        }
        return buffer;
    }, []);

    // Setup WebSocket connection to AssemblyAI v3
    const setupWebSocket = useCallback((token) => {
        return new Promise((resolve, reject) => {
            const wsUrl = `wss://streaming.assemblyai.com/v3/ws?sample_rate=${SAMPLE_RATE}&encoding=pcm_s16le&format_turns=true&token=${token}`;
            const ws = new WebSocket(wsUrl);

            const timeout = setTimeout(() => {
                ws.close();
                reject(new Error('Connection timeout'));
            }, 10000);

            ws.onopen = () => {
                clearTimeout(timeout);
                console.log('WebSocket connected to AssemblyAI v3');
                setConnectionStatus('connected');
                setIsReconnecting(false);
                reconnectAttemptsRef.current = 0;
                resolve(ws);
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);

                    if (data.type === 'Begin') {
                        console.log('Streaming session started:', data.id);
                        return;
                    }

                    if (data.type === 'Termination') {
                        console.log('Session terminated. Duration:', data.audio_duration_seconds);
                        return;
                    }

                    // Handle Turn messages (transcript data)
                    if (data.transcript !== undefined) {
                        const text = data.transcript;

                        if (data.end_of_turn && data.turn_is_formatted) {
                            // Final formatted transcript for this turn
                            if (text) {
                                setTranscript(prev => {
                                    const newTranscript = prev ? `${prev} ${text}` : text;
                                    onFinalTranscript?.(newTranscript);
                                    return newTranscript;
                                });
                                setInterimTranscript('');
                            }
                        } else if (!data.end_of_turn) {
                            // Interim transcript
                            setInterimTranscript(text);
                            onTranscript?.(text, false);
                        }
                    }
                } catch (e) {
                    console.error('Error parsing WebSocket message:', e);
                }
            };

            ws.onerror = (err) => {
                clearTimeout(timeout);
                console.error('WebSocket error:', err);
                setConnectionStatus('error');
                if (!isStoppingRef.current) {
                    onError?.(new Error('WebSocket connection error'));
                }
                reject(err);
            };

            ws.onclose = (event) => {
                console.log('WebSocket closed:', event.code, event.reason);
                setConnectionStatus('disconnected');
                if (!isStoppingRef.current && event.code !== 1000) {
                    attemptReconnect();
                }
            };
        });
    }, [onTranscript, onFinalTranscript, onError]);

    // Setup audio capture and send to WebSocket
    const setupAudioCapture = useCallback((stream, ws) => {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: SAMPLE_RATE,
        });
        audioContextRef.current = audioContext;

        const source = audioContext.createMediaStreamSource(stream);
        sourceRef.current = source;

        // Use ScriptProcessorNode for wider browser support
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        processorRef.current = processor;

        processor.onaudioprocess = (e) => {
            if (ws.readyState === WebSocket.OPEN && !isStoppingRef.current) {
                const inputData = e.inputBuffer.getChannelData(0);
                const pcm16 = floatTo16BitPCM(inputData);
                ws.send(pcm16);
            }
        };

        source.connect(processor);
        processor.connect(audioContext.destination);
    }, [floatTo16BitPCM]);

    // Attempt to reconnect
    const attemptReconnect = useCallback(async () => {
        if (isStoppingRef.current) return;

        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
            setError(`Connection failed after ${MAX_RECONNECT_ATTEMPTS} attempts. Please try again.`);
            setIsReconnecting(false);
            setConnectionStatus('error');
            cleanup();
            setIsRecording(false);
            return;
        }

        setIsReconnecting(true);
        reconnectAttemptsRef.current += 1;
        onReconnecting?.(reconnectAttemptsRef.current);

        console.log(`Reconnection attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS}`);

        await new Promise(resolve => setTimeout(resolve, RECONNECT_DELAY));

        if (isStoppingRef.current) return;

        try {
            // Close existing WebSocket
            if (wsRef.current) {
                try { wsRef.current.close(); } catch (e) { /* ignore */ }
                wsRef.current = null;
            }

            // Close existing audio processing
            if (processorRef.current) {
                try { processorRef.current.disconnect(); } catch (e) { /* ignore */ }
                processorRef.current = null;
            }
            if (sourceRef.current) {
                try { sourceRef.current.disconnect(); } catch (e) { /* ignore */ }
                sourceRef.current = null;
            }
            if (audioContextRef.current) {
                try { audioContextRef.current.close(); } catch (e) { /* ignore */ }
                audioContextRef.current = null;
            }

            // Get new token
            const tokenResponse = await api.get('/api/speech/realtime-token');
            if (!tokenResponse.data.success) {
                throw new Error(tokenResponse.data.message || 'Failed to get token');
            }

            const ws = await setupWebSocket(tokenResponse.data.token);
            wsRef.current = ws;

            // Restart audio capture if stream still exists
            if (streamRef.current && streamRef.current.active) {
                setupAudioCapture(streamRef.current, ws);
            }

            setConnectionStatus('connected');
            setIsReconnecting(false);
            setError(null);
            reconnectAttemptsRef.current = 0;
        } catch (err) {
            console.error('Reconnect failed:', err);
            attemptReconnect();
        }
    }, [cleanup, onReconnecting, setupWebSocket, setupAudioCapture]);

    // Start recording
    const startRecording = useCallback(async () => {
        try {
            isStoppingRef.current = false;
            reconnectAttemptsRef.current = 0;
            setError(null);
            setTranscript('');
            setInterimTranscript('');
            setConnectionStatus('connecting');

            // Get token from server
            const tokenResponse = await api.get('/api/speech/realtime-token');
            if (!tokenResponse.data.success) {
                throw new Error(tokenResponse.data.message || 'Failed to get token');
            }

            // Get microphone access
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: SAMPLE_RATE,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                }
            });
            streamRef.current = stream;

            // Setup WebSocket
            const ws = await setupWebSocket(tokenResponse.data.token);
            wsRef.current = ws;

            // Setup audio capture
            setupAudioCapture(stream, ws);
            setIsRecording(true);
        } catch (err) {
            console.error('Failed to start recording:', err);

            let errorMessage = 'Failed to start recording';
            if (err.name === 'NotAllowedError') {
                errorMessage = 'Microphone permission denied. Please allow microphone access and try again.';
            } else if (err.name === 'NotFoundError') {
                errorMessage = 'No microphone found. Please connect a microphone and try again.';
            } else if (err.name === 'NotReadableError') {
                errorMessage = 'Microphone is in use by another application.';
            } else if (err.response?.status === 500) {
                errorMessage = err.response?.data?.message || 'Dictation service unavailable. Please try again later.';
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
            setConnectionStatus('error');
            onError?.(new Error(errorMessage));
            cleanup();
        }
    }, [setupWebSocket, setupAudioCapture, cleanup, onError]);

    // Stop recording
    const stopRecording = useCallback(() => {
        isStoppingRef.current = true;
        cleanup();
        setIsRecording(false);
        setIsReconnecting(false);
    }, [cleanup]);

    // Clear transcript
    const clearTranscript = useCallback(() => {
        setTranscript('');
        setInterimTranscript('');
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isStoppingRef.current = true;
            cleanup();
        };
    }, [cleanup]);

    return {
        isRecording,
        transcript,
        interimTranscript,
        fullTranscript: transcript + (interimTranscript ? ` ${interimTranscript}` : ''),
        startRecording,
        stopRecording,
        clearTranscript,
        error,
        isReconnecting,
        connectionStatus,
        reconnectAttempts: reconnectAttemptsRef.current,
    };
};

export default useRealtimeTranscription;
